import { supabase } from './supabase.js'
import { imageLibraryService } from './imageLibraryService.js'

class ImageService {
  constructor() {
    console.log('🎨 Image Service initialized - using Fal.ai via Supabase Edge Functions')
    console.log('🖼️ Model: fal-ai/flux-general')
    console.log('🔒 API key securely stored in Supabase secrets')
  }

  /**
   * Generate an image using AI via Supabase Edge Function
   * @param {Object} options - Generation options
   * @param {string} options.prompt - The image prompt
   * @param {string} options.type - Image type: 'cover', 'character', 'scene', 'custom'
   * @param {string} options.userId - User ID for image ownership
   * @param {string} [options.projectTitle] - Project title for context
   * @param {string} [options.genre] - Genre for enhanced prompts
   * @param {string} [options.projectId] - Project ID to associate with
   * @param {string} [options.slideId] - Slide ID to associate with
   * @param {Function} [options.onProgress] - Progress callback
   * @returns {Promise<Object>} Generated image data
   */
  async generateImage(options) {
    const { 
      prompt, 
      type = 'custom', 
      userId, 
      projectTitle, 
      genre, 
      projectId, 
      slideId,
      onProgress 
    } = options

    if (!prompt || !userId) {
      throw new Error('Prompt and userId are required')
    }

    try {
      console.log('🎨 Generating image via Supabase Edge Function...')
      console.log('📝 Prompt:', prompt)
      console.log('🎯 Type:', type)
      
      if (onProgress) {
        onProgress('Preparing image generation...')
      }

      // Call the Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('generate-image', {
        body: {
          prompt,
          type,
          userId,
          projectTitle,
          genre
        }
      })

      if (error) {
        console.error('Supabase Edge Function error:', error)
        throw new Error(`Image generation failed: ${error.message}`)
      }

      if (data.fallback) {
        console.warn('🚨 Image service returned fallback response')
        console.warn('This could be due to: API key issues, rate limits, or Edge Function errors')
        throw new Error('Image generation service temporarily unavailable')
      }

      if (!data.success || !data.image) {
        console.error('❌ No image returned from service')
        throw new Error('Failed to generate image')
      }

      const imageData = data.image
      console.log('✅ Image generated successfully:', imageData.url)

      if (onProgress) {
        onProgress('Saving image to database...')
      }

      // Save the generated image to the database
      const savedImage = await this.saveGeneratedImage({
        ...imageData,
        projectId,
        slideId
      })

      if (onProgress) {
        onProgress('Saving to your image library...')
      }

      // Save to image library
      try {
        const libraryImage = await imageLibraryService.saveAIGeneratedImage({
          userId: imageData.userId,
          projectId: imageData.projectId,
          imageUrl: imageData.url,
          width: imageData.width,
          height: imageData.height,
          prompt: imageData.prompt,
          type: imageData.type,
          aiGeneratedImageId: savedImage.id
        })

        console.log('📚 Image saved to library:', libraryImage?.id)
        
        // Return the saved image with library reference
        return {
          ...savedImage,
          libraryImageId: libraryImage?.id
        }
      } catch (libraryError) {
        console.warn('⚠️ Failed to save to library, but image generation succeeded:', libraryError)
        // Don't throw - the main generation was successful
      }

      if (onProgress) {
        onProgress('Image generation complete!')
      }

      return savedImage

    } catch (error) {
      console.error('💥 Image generation error:', error)
      throw error
    }
  }

  /**
   * Save generated image metadata to database
   * @param {Object} imageData - Image data to save
   * @returns {Promise<Object>} Saved image record
   */
  async saveGeneratedImage(imageData) {
    try {
      console.log('💾 Saving generated image to database...')

      const { data, error } = await supabase
        .from('ai_generated_images')
        .insert([{
          user_id: imageData.userId,
          project_id: imageData.projectId,
          slide_id: imageData.slideId,
          element_id: imageData.elementId,
          image_url: imageData.url,
          width: imageData.width,
          height: imageData.height,
          original_prompt: imageData.prompt,
          enhanced_prompt: imageData.enhancedPrompt,
          image_type: imageData.type,
          project_title: imageData.projectTitle,
          genre: imageData.genre,
          model_used: 'fal-ai/flux-general'
        }])
        .select()
        .single()

      if (error) {
        console.error('Database save error:', error)
        throw new Error(`Failed to save image: ${error.message}`)
      }

      console.log('✅ Image saved to database:', data.id)
      return {
        ...imageData,
        id: data.id,
        dbRecord: data
      }

    } catch (error) {
      console.error('💥 Database save error:', error)
      throw error
    }
  }

  /**
   * Get generated images for a user
   * @param {string} userId - User ID
   * @param {string} [projectId] - Optional project filter
   * @returns {Promise<Array>} Array of generated images
   */
  async getUserImages(userId, projectId = null) {
    try {
      let query = supabase
        .from('ai_generated_images')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (projectId) {
        query = query.eq('project_id', projectId)
      }

      const { data, error } = await query

      if (error) {
        console.error('Error fetching user images:', error)
        throw new Error(`Failed to fetch images: ${error.message}`)
      }

      return data || []

    } catch (error) {
      console.error('💥 Error fetching user images:', error)
      throw error
    }
  }

  /**
   * Delete a generated image
   * @param {string} imageId - Image ID to delete
   * @param {string} userId - User ID for security
   * @returns {Promise<boolean>} Success status
   */
  async deleteImage(imageId, userId) {
    try {
      const { error } = await supabase
        .from('ai_generated_images')
        .delete()
        .eq('id', imageId)
        .eq('user_id', userId)

      if (error) {
        console.error('Error deleting image:', error)
        throw new Error(`Failed to delete image: ${error.message}`)
      }

      console.log('✅ Image deleted successfully')
      return true

    } catch (error) {
      console.error('💥 Error deleting image:', error)
      throw error
    }
  }

  /**
   * Generate a cover image for a project
   * @param {Object} options - Cover image options
   * @param {string} options.projectTitle - Project title
   * @param {string} options.genre - Project genre
   * @param {string} options.synopsis - Project synopsis for context
   * @param {string} options.userId - User ID
   * @param {string} options.projectId - Project ID
   * @param {Function} [options.onProgress] - Progress callback
   * @returns {Promise<Object>} Generated cover image
   */
  async generateCoverImage(options) {
    const { projectTitle, genre, synopsis, userId, projectId, onProgress, visualStyle } = options

    // Create a detailed prompt for the cover image
    const styleInfo = visualStyle || { style: 'Feature film cinematic style', description: 'professional cinematic quality with dramatic lighting' }
    const coverPrompt = `${styleInfo.style} movie poster for "${projectTitle}", ${genre} genre. 
    Based on: ${synopsis.substring(0, 200)}...
    Visual Style: ${styleInfo.description}. Create a compelling poster that captures the essence of this ${genre} production.
    Consistent visual style throughout the project.
    
    IMPORTANT: NO TEXT, NO TITLES, NO WORDS on the image. Pure visual content only in ${styleInfo.style}.`

    return await this.generateImage({
      prompt: coverPrompt,
      type: 'cover',
      userId,
      projectTitle,
      genre,
      projectId,
      onProgress
    })
  }

  /**
   * Generate a thumbnail image for a project
   * @param {Object} options - Thumbnail generation options
   * @param {string} options.projectTitle - Project title
   * @param {string} options.genre - Project genre
   * @param {string} options.description - Project description
   * @param {string} options.userId - User ID
   * @param {string} options.projectId - Project ID
   * @param {Function} [options.onProgress] - Progress callback
   * @returns {Promise<Object>} Generated thumbnail image
   */
  async generateThumbnail(options) {
    const { projectTitle, genre, description, userId, projectId, onProgress, visualStyle } = options

    // Create a concise prompt for the thumbnail
    const styleInfo = visualStyle || { style: 'Feature film cinematic style', description: 'professional cinematic quality' }
    const thumbnailPrompt = `${styleInfo.style} thumbnail for "${projectTitle}", ${genre || 'cinematic'} project. 
    ${description ? `Project about: ${description.substring(0, 150)}...` : ''}
    Visual Style: ${styleInfo.description}. Create a clean, professional thumbnail suitable for project listings. 
    16:9 aspect ratio, high quality, suitable for small display sizes.
    Consistent with ${styleInfo.style} throughout the project.
    
    IMPORTANT: NO TEXT, NO TITLES, NO WORDS on the image. Pure visual content only in ${styleInfo.style}.`

    return await this.generateImage({
      prompt: thumbnailPrompt,
      type: 'thumbnail',
      userId,
      projectTitle,
      genre,
      projectId,
      onProgress
    })
  }
}

// Create and export a singleton instance
export const imageService = new ImageService()
